// RIVCHPP referral chain (by users.phc_type of the facility that refers):
//   Primary   -> Secondary
//   Secondary -> Secondary, Tertiary
//   Tertiary  -> none (end of the chain)
// Secondary and tertiary facilities may also refer to themselves (e.g. to another department or for services).
// Telemedicine providers refer like primary care. Used by AuthorizationCode.vue and AddRecord.vue to build the
// "receiving facility" dropdown.

const CHAIN = {
  primary: ["secondary"],
  telemedicine: ["secondary"],
  secondary: ["secondary", "tertiary"],
  tertiary: [],
};

export const normLevel = (level) => String(level || "").trim().toLowerCase();

/** Levels a facility of `fromLevel` may refer to; null when its level isn't set (any secondary/tertiary). */
export function allowedLevels(fromLevel) {
  const level = normLevel(fromLevel);
  return Object.prototype.hasOwnProperty.call(CHAIN, level) ? CHAIN[level] : null;
}

/** The facility (users.id) the signed-in facility user works for, or null for agency staff. */
export function ownFacilityId(user) {
  if (!user) return null;
  if (user.type === "provider") return user.id;
  if (user.type === "provider_employee") return user.institutional_id;
  return null;
}

/** Care level of a facility: looked up in the agency's facility list, else from the login payload. */
export function facilityLevel(facilityId, providers, user) {
  const facility = (providers || []).find((p) => Number(p.id) === Number(facilityId));
  if (facility && facility.phc_type) return facility.phc_type;
  if (user && Number(ownFacilityId(user)) === Number(facilityId)) {
    if (user.type === "provider") return user.phc_type || null;
    return (user.staffprovider && user.staffprovider.phc_type) || null;
  }
  return null;
}

/** Levels that may refer to themselves. */
const SELF_REFERRAL = ["secondary", "tertiary"];

/** Facilities a facility at `fromLevel` may refer to: by the chain, plus itself for secondary/tertiary. */
export function receivingFacilities(providers, fromLevel, fromFacilityId) {
  const allowed = allowedLevels(fromLevel) || ["secondary", "tertiary"];
  const selfAllowed = SELF_REFERRAL.includes(normLevel(fromLevel));
  return (providers || []).filter((p) => {
    if (Number(p.id) === Number(fromFacilityId)) return selfAllowed;
    return allowed.includes(normLevel(p.phc_type));
  });
}

/** A short line under the dropdown explaining what it offers. */
export function referralHint(fromLevel) {
  const level = normLevel(fromLevel);
  if (level === "tertiary") return "Tertiary facilities are the end of the referral chain; they can refer within their own facility only.";
  if (level === "secondary") return "Secondary facilities refer to secondary or tertiary facilities, including themselves.";
  if (level === "primary" || level === "telemedicine") return "Primary care refers to secondary facilities only.";
  return "The referring facility's care level isn't set; showing secondary and tertiary facilities.";
}
